import {EventListener, EventVerteiler} from "../global/eventVerteiler";
import type {TextObject} from "./selectableObjects";
import {FONTEVENTNAMES, SELOBEVENTNAMES} from "../global/GlobalEventnames";
import globalsObject, {handleError} from "./globalsObject";
import {SelObTypes} from "./SelObTypes";
import {HTMLPrinter} from "../global/htmlprinter";

declare class FontFace {
  constructor(name, url)
    load()
    status
}

export type FontGroups = {
    standardFonts: FontInformation[],
    legacyFonts: FontInformation[],
    userSpecificFonts: FontInformation[],
    kkSpecificFonts: FontInformation[]
};

export type FontsPHPResponseClass = {
    fontGroups: FontGroups,
    nonStandardFontsCSS,
    firstFontCSS
};

export type FontInformation = {
    Font:string,
    FontID:string,
    outlineLineJoin,
    auwiFontsReplicationID:number,
    visible,
    loaded,
    url:string
};



export interface IFontStorageWrapper {
    getFontLoadedStatus(fontID);
    getFontInfoFromFontName(fontname): FontInformation|undefined;
    createFontList(language);
    getFlattenedFontList();
    fontsAreLoaded():boolean;
    getFirstAvailablFontName():string|null;
    setFontInfoLoadingStatus(fontName:string, loadingstatus:boolean);
}

export class FontStorageWrapperKonfi implements IFontStorageWrapper {
    private static readonly LOADERURL = "/konfigurator_php/designmodul/fonts.php";
    private fonts: FontInformation[] = [];
    private loadStatus = false;



    getFontLoadedStatus(fontname) {
        const fontInfo: FontInformation|undefined = this.fonts.find(f => f.Font === fontname);
        if(fontInfo === undefined) {
            return false;
        }
        return fontInfo.loaded;
    }

    reset() {
        globalsObject.resourceManager.executeInvalidation("fontsreload");
        this.loadStatus = false;
        EventVerteiler.broadcast(FONTEVENTNAMES.RESET);
    }

    getFontInfoFromFontName(fontname): FontInformation|undefined {
        return this.fonts.find(f => f.Font === fontname);
    }

    createFontList(language) {
        language = globalsObject.controller.model.getSprache();
        this.reset();
        $.ajax({
            type: "GET",
            url: FontStorageWrapperKonfi.LOADERURL + "?language=" + language,
            dataType: 'json',
            error: FontStorageWrapperKonfi.handleLoadingError,
            success: (response)=>this.processLoadedFonts(response)
        });
    }

    private static handleLoadingError = () => handleError("ajaxFail", "createNewFontList", "konf_error_noFontsLoaded");

    private processLoadedFonts(response: FontsPHPResponseClass) {
        this.loadStatus = true;

        setTimeout(()=>{
            this.fonts = response.fontGroups.standardFonts.concat(response.fontGroups.userSpecificFonts).concat(response.fontGroups.legacyFonts).concat(response.fontGroups.kkSpecificFonts);
            const fontListforExternals = this.createFontlistForExternals();
            EventVerteiler.broadcast(FONTEVENTNAMES.FONTSLOADED, {fontgroups:response.fontGroups});
            EventVerteiler.broadcast("fontsReadyEvent", {fontList: fontListforExternals});
            globalsObject.resourceManager.registerResource({
                resourceName: "fonts",
                invalidatedWhen: "fontsreload",
                data: fontListforExternals,
                startingStati: ["loaded"]
            });
        }, 20);
    }

    private createFontlistForExternals(): { font, fontID, outlineLineJoin }[] {
        return this.fonts.map((row: FontInformation) => {
            return {font: row.Font, fontID: row.FontID, outlineLineJoin: row.outlineLineJoin};
        });
    }

    getFlattenedFontList() {
        return this.fonts;
    }

    fontsAreLoaded(): boolean {
        return this.loadStatus;
    }

    getFirstAvailablFontName(): string | null {
        if(this.fonts.length>0) {
            return this.fonts[0].Font
        }
        return null;
    }
    setFontInfoLoadingStatus(fontName:string, loadingStatus:boolean) {
        const fontInfo = this.fonts.find(f=>f.Font === fontName);
        if(fontInfo === undefined) {
            return;
        }
        fontInfo.loaded = loadingStatus;
    }
}

export class FontHandler extends EventListener {
    private static fontHandlerStore: IFontStorageWrapper;

    public constructor(fontHandlerStore:IFontStorageWrapper) {
        super();
        FontHandler.fontHandlerStore = fontHandlerStore;
        this.initEventListeners();
    }

    public static surroundWithSingleQuotes(font:string):string{
        return HTMLPrinter.quoteSingle(font.replace("'", "\'"));
    }

    private initEventListeners() {
        const self = FontHandler;
        this.removeAllEventListeners()
            .addEventListener(SELOBEVENTNAMES.CHANGED, (data) => self.writeFontCodeToTextEditTab(data.selOb))
            .addEventListener(SELOBEVENTNAMES.SELECTED, (selOb: TextObject) => self.writeFontCodeToTextEditTab(selOb))
            .addEventListener("initFontsEvent", (data) => self.createNewFontList(data.language))
    }

    private static createNewFontList(language) {
        FontHandler.fontHandlerStore.createFontList(language)
    }

    public static getFontLoadedStatus(fontID) {
        return FontHandler.fontHandlerStore.getFontLoadedStatus(fontID);
    }

    private static renderFont(fontInfo: FontInformation) {
        EventVerteiler.broadcast("prerenderFontEvent", fontInfo)
    }

    public static loadAdobeNoDefFont() {
        const url = 'url(/konfigurator_html/fonts/owayo/adobe-notdef.woff)';
        const fontToLoad = new FontFace('adobe notdef', url);
        fontToLoad.load().then(function (loaded_face) {
            (document as unknown as {fonts}).fonts.add(loaded_face);
            setTimeout(function () {
                globalsObject.resourceManager.registerLoadedResourceWithoutData("fontloaded_adobe-notdef");
            }, 50);


        }).catch(function (error) {
            console.log("Error loading font:  Adobe notdef" + error);
        });
    }

    private static fontLoaded(fontInfo, loaded_face) {
        FontHandler.fontHandlerStore.setFontInfoLoadingStatus(fontInfo.Font, true);
            //console.log("Font loading status after load: "+fontInfo.FontID+' - '+fontToLoad.status);
            (document as unknown as {fonts}).fonts.add(loaded_face);
            FontHandler.renderFont(fontInfo);
            setTimeout(function () {
                globalsObject.resourceManager.registerLoadedResourceWithoutData("fontloaded_" + fontInfo.Font);
                // EventVerteiler.broadcast("singleFontsReadyEvent", fontInfo);
            }, 50);
    }


    static loadFontToDocumentViaName(font: string) {
        const fontInfo = FontHandler.fontHandlerStore.getFontInfoFromFontName(font);
        if(fontInfo === undefined) {
            throw new Error("sorry, I dont't recognize the font " + font + "; the textobject will not be displayed correctly!");
        }
        if(fontInfo.loaded) {
            return;
        }
        FontHandler.loadFontToDocument(fontInfo);
    }

    public static loadFontToDocument(fontInfo:FontInformation){
        const url = 'url(/' + fontInfo.url + ')';
        const fontToLoad = new FontFace(FontHandler.surroundWithSingleQuotes(fontInfo.Font), url);
        fontToLoad.load().then(function(loaded_face) {
            FontHandler.fontLoaded(fontInfo, loaded_face);
        }).catch(function (error) {
            FontHandler.fontHandlerStore.setFontInfoLoadingStatus(fontInfo.Font, false);
            console.log("Error loading font: " + fontInfo.FontID + ' - ' + error);
        });

        setTimeout(function () {
            if (fontToLoad.status === "unloaded") {
                //console.log("Font was not loaded: "+fontInfo.FontID+' - '+fontToLoad.status);
                FontHandler.loadFontToDocument(fontInfo);
            }
        }, 1000);
    }

    private static writeFontCodeToTextEditTab(selOb: TextObject) {
        if (selOb.type !== SelObTypes.TEXT || FontHandler.fontHandlerStore.fontsAreLoaded() === false) {
            return;
        }
        EventVerteiler.broadcast(FONTEVENTNAMES.UPDATEFONTINSELOB, {font:selOb.font, fontcode:FontHandler.getFontCode(selOb.font)})
    }

    private static getFont(fontName) {
        return FontHandler.fontHandlerStore.getFontInfoFromFontName(fontName) || null;
    }

    public static getOutlineLineJoin(fontname) {
        const font = FontHandler.getFont(fontname);
        return font === null ? null : font.outlineLineJoin;
    }

    private static getFontCode(fontName) {
        const font = FontHandler.getFont(fontName);
        return font !== null ? font.FontID : "";
    }

    static isAvailableFont(font: string) {
        return FontHandler.getFont(font) !== null;
    }

    static getFirstAvailableFont() {
        return FontHandler.fontHandlerStore.getFirstAvailablFontName();
    }
}
